/**
 * Hook for managing profile picture uploads and deletions.
 */
import { useState } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import * as settingsApi from '../services/settingsApi';

export function useProfilePicture() {
  const { session } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadPicture = async (file: File): Promise<string> => {
    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }

    try {
      setUploading(true);
      setError(null);
      const result = await settingsApi.uploadProfilePicture(session.access_token, file);
      return result.profile_picture_url;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload picture';
      setError(message);
      throw new Error(message);
    } finally {
      setUploading(false);
    }
  };

  const deletePicture = async (): Promise<void> => {
    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }

    try {
      setError(null);
      await settingsApi.deleteProfilePicture(session.access_token);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete picture';
      setError(message);
      throw new Error(message);
    }
  };

  return {
    uploading,
    error,
    uploadPicture,
    deletePicture,
  };
}

